import { ChangeEvent, DragEventHandler, useCallback, useContext, useEffect, useRef, useState } from "react";
import classes from "./classes.module.scss";
import { FormContext } from "../FormContext";
import Typography from "components/elements/Typography";
import classNames from "classnames";
import I18n from "components/materials/I18n";
import { CheckIcon, TrashIcon } from "@heroicons/react/24/solid";

type IProps = {
	name: string;
	keyErrorName?: string;
	label?: string;
	required?: boolean;
	accept: string[];
	description: string;
	sub_description?: string;
	existingFileName?: string;
	disabled?: boolean;
	maxMbSize?: number;
	formRef?: React.RefObject<HTMLFormElement>;
	onDrop?: (file: File) => void;
	onFileChange?: (file: File) => void;
	icon?: React.ReactNode;
	className?: string;
	defaultFile?: File | null;
};

export default function DragAndDropFile(props: IProps) {
	const inputRef = useRef<HTMLInputElement>(null);
	const [isDragOver, setIsDragOver] = useState(false);
	const [isFileTooBig, setIsFileTooBig] = useState(false);
	const [file, setFile] = useState<File | null>(null);
	const context = useContext(FormContext);
	let errors = context?.getMessagesErrors(props.keyErrorName ?? props.name);
	const hasErrors = errors?.length > 0;
	const accept = props.accept.join(",");

	const isFileSizeValid = useCallback(() => {
		if (!props.maxMbSize || !file) return true;
		const maxSizeInBytes = props.maxMbSize * 1024 * 1024;
		return file.size <= maxSizeInBytes;
	}, [props.maxMbSize, file]);

	const onClick = useCallback(() => {
		if (!inputRef.current) return;
		inputRef.current.click();
	}, []);

	const handleFileChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			const fileList = event.target.files;
			if (fileList && fileList[0]) {
				setFile(fileList[0]);
				props.onFileChange?.(fileList[0]);
			}
		},
		[props],
	);

	const handleDragOver: DragEventHandler<HTMLDivElement> = (event) => {
		event.preventDefault();
		if (!isDragOver) setIsDragOver(true);
	};

	const handleDragEnter: DragEventHandler<HTMLDivElement> = (event) => {
		event.preventDefault();
		setIsDragOver(true);
	};

	const handleDragLeave: DragEventHandler<HTMLDivElement> = (event) => {
		event.preventDefault();
		setIsDragOver(false);
	};

	const handleDrop: DragEventHandler<HTMLDivElement> = useCallback(
		(event) => {
			event.preventDefault();
			setIsDragOver(false);
			const files = event.dataTransfer.files;
			if (files && files[0]) {
				const form = props.formRef?.current;
				const input = form?.querySelector<HTMLInputElement>(`input[name="${props.name}"]`);

				if (input) input.files = files;

				setFile(files[0]);
				props.onDrop?.(files[0]);
			}
		},
		[props],
	);

	const removeFile = useCallback(() => {
		setFile(null);
		if (inputRef.current) {
			const fileList = new DataTransfer();
			inputRef.current.files = fileList.files;
		}
	}, [inputRef]);

	useEffect(() => {
		if (!file) return;
		const isValid = isFileSizeValid();
		setIsFileTooBig(!isValid);
		if (!isValid) setFile(null);
	}, [file, isFileSizeValid]);

	useEffect(() => {
		if (!props.defaultFile) return;
		if (!props.formRef) {
			console.error("Form ref is mandatory to set default file");
			return;
		}

		setFile(props.defaultFile);
		const form = props.formRef.current;
		const input = form?.querySelector<HTMLInputElement>(`input[name="${props.name}"]`);

		const fileList = new DataTransfer();
		fileList.items.add(props.defaultFile);
		if (input) input.files = fileList.files;
	}, [props.defaultFile, props.formRef, props.name]);

	return (
		<div className={classes["root"]} onDragOver={handleDragOver} onDragEnter={handleDragEnter} onDragLeave={handleDragLeave} onDrop={handleDrop}>
			{props.label && (
				<Typography type="span" weight="regular" size="medium">
					{props.label}
				</Typography>
			)}
			<input
				type="file"
				name={props.name}
				accept={accept}
				className={classes["input"]}
				required={props.required}
				onChange={handleFileChange}
				ref={inputRef}
				disabled={props.disabled}
			/>

			{!file && (
				<label
					htmlFor={props.name}
					className={classNames(classes["drag-and-drop-container"], props.className, {
						[classes["drag-over"]!]: isDragOver,
					})}
					onClick={onClick}>
					{props.icon}
					<Typography type="p" weight="regular" size="medium">
						{props.description}
					</Typography>
					{props.sub_description && (
						<Typography type="p" weight="regular" size="medium" className={classes["accept"]}>
							{props.sub_description}
						</Typography>
					)}
				</label>
			)}

			{file && (
				<div className={classes["file-container"]}>
					<div className={classes["check-icon-container"]}>
						<CheckIcon className={classes["check-icon"]} />
					</div>
					<Typography type="p" weight="regular" size="medium">
						{file?.name ? file?.name : props.existingFileName ? props.existingFileName : I18n.trslt(I18n.asset.component.drag_and_drop_file.no_file_selected)}
					</Typography>
					<div className={classes["trash-icon-container"]}>
						<TrashIcon className={classes["trash-icon"]} onClick={removeFile} />
					</div>
				</div>
			)}

			{hasErrors && (
				<div className={classes["errors-container"]}>
					{errors.map((message, i) => (
						<Typography type="p" size="small" weight="medium" key={i} color="error">
							{message}
						</Typography>
					))}
				</div>
			)}
			{isFileTooBig && props.maxMbSize && (
				<div className={classes["errors-container"]}>
					<Typography type="p" size="small" weight="medium" color="error">
						{I18n.trslt(I18n.asset.component.drag_and_drop_file.file_too_big, { maxSize: props.maxMbSize.toString() })}
					</Typography>
				</div>
			)}
		</div>
	);
}
